import { Tab } from "@dewo/app/components/Tab";
import { usePermission } from "@dewo/app/contexts/PermissionsContext";
import { Card, Divider, Tabs, Typography } from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import React, { FC } from "react";
import { OrganizationRBAC } from "../../rbac/organization/OrganizationRBAC";
import { OrganizationProfileSettings } from "./OrganizationProfileSettings";
import { OrganizationIntegrations } from "./OrganizationIntegrations";
import { OrganizationSettingsManage } from "./OrganizationSettingsManage";
import _ from "lodash";
import { OrganizationGeneralSettings } from "./OrganizationGeneralSettings";
import { OrganizationRoles } from "../../rbac/organization/OrganizationRoles";
import {
  AppstoreOutlined,
  InfoCircleOutlined,
  LockOutlined,
  SettingOutlined,
  ShareAltOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";

interface Props {
  organizationId: string;
  currentTab: string;
  onTabClick(tab: string): void;
}

export const OrganizationSettings: FC<Props> = ({
  currentTab,
  organizationId,
  onTabClick,
}) => {
  const canDeleteOrganization = usePermission("delete", "Organization");
  const screens = useBreakpoint();
  if (_.isEmpty(screens)) return null;
  return (
    <Tabs
      type="card"
      tabPosition={screens.md ? "left" : "top"}
      className="dewo-settings-tabs"
      activeKey={currentTab}
      onTabClick={onTabClick}
      renderTabBar={(props, TabBar) => (
        <Card
          size="small"
          title="Settings"
          style={{ marginBottom: screens.md ? 0 : 24 }}
          bodyStyle={{ padding: 0 }}
        >
          <TabBar {...props} tabPosition="left" />
        </Card>
      )}
    >
      <Tabs.TabPane
        tab={<Tab icon={<AppstoreOutlined />} children="General" />}
        key="general"
      >
        <OrganizationGeneralSettings organizationId={organizationId} />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={<Tab icon={<InfoCircleOutlined />} children="Profile" />}
        key="profile"
      >
        <OrganizationProfileSettings organizationId={organizationId} />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={<Tab icon={<UsergroupAddOutlined />} children="Roles" />}
        key="roles"
      >
        <OrganizationRoles organizationId={organizationId} />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={<Tab icon={<LockOutlined />} children="Permissions" />}
        key="access"
      >
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          Permissions
        </Typography.Title>
        <Divider style={{ marginTop: 0 }} />
        <OrganizationRBAC organizationId={organizationId} />
      </Tabs.TabPane>
      <Tabs.TabPane
        tab={<Tab icon={<ShareAltOutlined />} children="Integrations" />}
        key="integrations"
      >
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          Integrations
        </Typography.Title>
        <Divider style={{ marginTop: 0 }} />
        <OrganizationIntegrations organizationId={organizationId} />
      </Tabs.TabPane>
      {canDeleteOrganization && (
        <Tabs.TabPane
          tab={<Tab icon={<SettingOutlined />} children="Manage" />}
          key="manage"
        >
          <OrganizationSettingsManage organizationId={organizationId} />
        </Tabs.TabPane>
      )}
    </Tabs>
  );
};
